import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronRight, ChevronLeft } from 'lucide-react'

const slides = [
    {
        icon: '💰',
        title: 'Welcome to Spendly',
        subtitle: 'Track • Save • Grow',
        description: 'Your smart companion for managing finances effortlessly'
    },
    {
        icon: '📊',
        title: 'Smart Budget Categories',
        description: 'Organize your spending with customizable budget categories based on your salary'
    },
    {
        icon: '🚀',
        title: 'Instant UPI Payments',
        description: 'Make quick payments directly from your budget categories with saved UPI IDs'
    },
    {
        icon: '📈',
        title: 'Track Your Spending',
        description: 'Visualize your financial health with detailed analytics and insights'
    }
]

function Onboarding() {
    const [currentSlide, setCurrentSlide] = useState(0)
    const navigate = useNavigate()

    const handleNext = () => {
        if (currentSlide < slides.length - 1) {
            setCurrentSlide(currentSlide + 1)
        } else {
            handleGetStarted()
        }
    }

    const handlePrevious = () => {
        if (currentSlide > 0) {
            setCurrentSlide(currentSlide - 1)
        }
    }

    const handleSkip = () => {
        localStorage.setItem('onboardingCompleted', 'true')
        navigate('/login')
    }

    const handleGetStarted = () => {
        localStorage.setItem('onboardingCompleted', 'true')
        navigate('/login')
    }

    const isLastSlide = currentSlide === slides.length - 1

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-primary-900 to-secondary-900 flex flex-col justify-between p-3 sm:p-4 md:p-6">
            <div className="w-full max-w-2xl mx-auto flex flex-col justify-between min-h-screen py-4 sm:py-6">
                {/* Skip Button */}
                <div className="flex justify-end mb-2 sm:mb-3 md:mb-4">
                    <button
                        onClick={handleSkip}
                        className="text-white/70 hover:text-white transition-colors text-sm md:text-base font-medium"
                    >
                        Skip
                    </button>
                </div>

                {/* Slide Content */}
                <div className="bg-white/10 backdrop-blur-lg rounded-2xl md:rounded-3xl p-6 sm:p-8 md:p-10 lg:p-12 shadow-2xl border border-white/20 flex-1 flex flex-col justify-center">
                    <div className="text-center space-y-6 sm:space-y-8 flex flex-col items-center justify-center">
                        {/* Icon */}
                        <div className="text-7xl sm:text-8xl md:text-9xl lg:text-[10rem] animate-bounce-slow">
                            {slides[currentSlide].icon}
                        </div>

                        {/* Title */}
                        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white px-2">
                            {slides[currentSlide].title}
                        </h1>

                        {/* Subtitle (only for first slide) */}
                        {slides[currentSlide].subtitle && (
                            <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-primary-200 font-medium">
                                {slides[currentSlide].subtitle}
                            </p>
                        )}

                        {/* Description */}
                        <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-300 max-w-lg px-4">
                            {slides[currentSlide].description}
                        </p>
                    </div>

                    {/* Progress Dots */}
                    <div className="flex justify-center items-center space-x-2 mt-8 sm:mt-10 md:mt-12 mb-6 sm:mb-8">
                        {slides.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => setCurrentSlide(index)}
                                className={`h-2.5 sm:h-3 rounded-full transition-all duration-300 ${index === currentSlide
                                        ? 'w-10 sm:w-12 bg-primary-400'
                                        : 'w-2.5 sm:w-3 bg-white/30 hover:bg-white/50'
                                    }`}
                                aria-label={`Go to slide ${index + 1}`}
                            />
                        ))}
                    </div>

                    {/* Navigation Buttons */}
                    <div className="flex items-center justify-between gap-3 sm:gap-4">
                        <button
                            onClick={handlePrevious}
                            disabled={currentSlide === 0}
                            className={`flex items-center space-x-2 px-5 sm:px-6 md:px-8 py-3 sm:py-3.5 md:py-4 rounded-xl text-base sm:text-lg md:text-xl font-medium transition-all ${currentSlide === 0
                                    ? 'opacity-0 cursor-not-allowed'
                                    : 'bg-white/10 hover:bg-white/20 text-white'
                                }`}
                        >
                            <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6" />
                            <span className="hidden sm:inline">Previous</span>
                        </button>

                        <button
                            onClick={handleNext}
                            className="flex items-center space-x-2 px-5 sm:px-6 md:px-8 py-3 sm:py-3.5 md:py-4 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white rounded-xl text-base sm:text-lg md:text-xl font-medium transition-all shadow-lg hover:shadow-xl"
                        >
                            <span>{isLastSlide ? 'Get Started' : 'Next'}</span>
                            <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6" />
                        </button>
                    </div>
                </div>

                {/* Footer */}
                <div className="text-center mt-4 sm:mt-5 md:mt-6">
                    <p className="text-white/50 text-sm md:text-base">
                        © {new Date().getFullYear()} Spendly • Track, Save & Grow
                    </p>
                </div>
            </div>
        </div>
    )
}

export default Onboarding
